import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { rxResource } from '@angular/core/rxjs-interop';

import { SizeIcon } from './icon.interface';
import { IconCacheService } from './icon-cache.service';

@Component({
  selector: 'ds-icon',
  template: `
    <span
      [class]="['ds-icon-container', 'ds-icon-' + $sizeIcon()]"
      [class.ds-color-inverted]="$colorInverted()"
      [class.ds-legacy-icon]="$isLegacy()"
      [innerHTML]="$iconSvg()">
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  /**
   * ================================================
   * Dependencies
   * ================================================
   */
  private readonly iconCacheService = inject(IconCacheService);
  /**
   * ================================================
   * Inputs
   * ================================================
   */
  $name = input.required<string>({ alias: 'name' });
  $sizeIcon = input<SizeIcon>('s', { alias: 'sizeIcon' });
  $isLegacy = input<boolean>(false, { alias: 'isLegacy' });
  $colorInverted = input<boolean>(false, { alias: 'colorInverted' });
  /**
   * ================================================
   * Computed
   * ================================================
   */
  /**
   * Get the size of the icon based on the sizeIcon input.
   * @returns {string} The size of the icon in pixels.
  */
  $_sizeIcon = computed(() => {
    switch (this.$sizeIcon()) {
      case 'auto': return '';
      case 'xs': return '16'; // Only for some legacy icons
      case 's': return '20';
      case 'm': return '24';
      case 'l': return '36';
      case 'xl': return '48';
      default: return '24';
    }
  });
  /**
   * Computed to get the icon path based on size and name.
   */
  $iconPath = computed(() => {
    const name = this.$name();
    const isLegacy = this.$isLegacy();
    const sizeIcon = this.$_sizeIcon();

    if (isLegacy) {
      return `assets/icons/${name}.svg`;
    }
    if (sizeIcon) {
      return `assets/design-system/icons/${sizeIcon}/${name}.svg`;
    }
    return `assets/design-system/icons/${name}.svg`;
  });
  /**
   * Resource to get the icon as SVG using the caching service.
   * This replaces httpResource with our cached implementation.
   */
  $iconResource = rxResource<SafeHtml, { iconPath: string }>({
    params: () => ({ iconPath: this.$iconPath() }),
    stream: ({ params }) => this.iconCacheService.getIcon(params.iconPath)
  });
  /**
   * Computed to get the cached SVG content.
   * The content is already sanitized by the IconCacheService.
   */
  $iconSvg = computed<SafeHtml>(() => this.$iconResource.value() ?? '');
}
